"use client"

import type React from "react"
import { useState, useRef } from "react"
import { Typography, Paper, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton } from "@mui/material"
import { CloudUpload, InsertDriveFile, Close } from "@mui/icons-material"
import styles from "./FileUploader.module.css"

interface FileUploaderProps {
  onFilesChange: (files: File[]) => void
  accept?: string
  maxSize?: number // in MB
  multiple?: boolean
  description?: string
  name?: string
  onFilesSelected?: (files: File[]) => void
  disabled?: boolean
  required?: boolean
}

const FileUploader: React.FC<FileUploaderProps> = ({
  onFilesChange,
  accept = "*",
  maxSize = 5,
  multiple = false,
  description,
  name = "fileUploader",
  onFilesSelected = () => { },
  disabled = false,
  required = false,
}) => {
  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    setError(null)

    // Check file size
    const oversizedFiles = selectedFiles.filter((file) => file.size > maxSize * 1024 * 1024)

    if (oversizedFiles.length > 0) {
      setError(`Some files exceed the maximum size of ${maxSize}MB`)
      return
    }

    let newFiles: File[]
    if (multiple) {
      newFiles = [...files, ...selectedFiles]
    } else {
      newFiles = selectedFiles
    }

    setFiles(newFiles)
    onFilesChange(newFiles)
  }

  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index)
    setFiles(newFiles)
    onFilesChange(newFiles)
    setError(null)

    // Reset the input value to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " bytes"
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB"
    else return (bytes / (1024 * 1024)).toFixed(2) + " MB"
  }

  return (
    <div className={styles.container}>
      <Paper variant="outlined" className={styles.dropzone} onClick={handleButtonClick}>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <CloudUpload className={styles.uploadIcon} />
        <Typography variant="body1" align="center">
          Click to upload or drag and drop
        </Typography>
        {description && (
          <Typography variant="body2" color="textSecondary" align="center">
            {description}
          </Typography>
        )}
        <Typography variant="caption" color="textSecondary" align="center">
          {multiple ? "Files" : "File"} up to {maxSize}MB
        </Typography>
      </Paper>

      {error && (
        <Typography variant="body2" color="error" className={styles.errorText}>
          {error}
        </Typography>
      )}

      {files.length > 0 && (
        <List className={styles.fileList}>
          {files.map((file, index) => (
            <ListItem key={index} className={styles.fileItem}>
              <InsertDriveFile className={styles.fileIcon} />
              <ListItemText primary={file.name} secondary={formatFileSize(file.size)} className={styles.fileText} />
              <ListItemSecondaryAction>
                <IconButton edge="end" aria-label="delete" onClick={() => removeFile(index)} size="small">
                  <Close />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}
    </div>
  )
}

export default FileUploader
